import React from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useVariant } from '../contexts/VariantContext';
import { useSEO } from '../contexts/SEOContext';
import { useDomainManager } from '../config';
import VariantImageSlider from '../components/variants/VariantImageSlider';
import VariantTestimonials from '../components/variants/VariantTestimonials';
import VariantFeatures from '../components/variants/VariantFeatures';
import VariantManager from '../components/variants/VariantManager';

const Home = () => {
  const { brand, domainConfig } = useTheme();
  const { variant } = useVariant();
  const { currentSEO } = useSEO();
  const domainManager = useDomainManager(brand?.id);

  // Get hero content for variants using unified configuration
  const getHeroContent = () => {
    // Try to get content from unified configuration first
    const heroContent = domainManager.getHeroContent();
    if (heroContent) {
      return {
        mainImage: heroContent.mainImage,
        secondaryImage: heroContent.secondaryImage,
        title: heroContent.title,
        subtitle: heroContent.subtitle,
        features: heroContent.features
      };
    }

    // Fallback to legacy system
    const { getDomainImageAuto } = require('../utils/imageManager');
    
    // Auto-detect hero images for all domains
    const mainImage = getDomainImageAuto(brand.id, 'hero-main', ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp']);
    const secondaryImage = getDomainImageAuto(brand.id, 'hero-secondary', ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp']);

    // Get domain-specific content (reuse logic from HeroWithPhotos)
    switch (brand.id) {
      case 'geotab-camera':
        return {
          mainImage,
          secondaryImage,
          title: 'Professional Fleet Camera Solutions',
          subtitle: 'Advanced camera systems for fleet safety and security',
          features: ['HD Recording', 'Night Vision', 'GPS Tracking', 'Cloud Storage']
        };
      case 'geotab-dashcams':
      case 'geotab-dashcam':
        return {
          mainImage,
          secondaryImage,
          title: 'Premium Dash Cam Solutions',
          subtitle: 'High-quality dash cameras for professional drivers',
          features: ['4K Recording', 'Impact Detection', 'Driver Alerts', 'Easy Installation']
        };
      case 'geotabstore':
        return {
          mainImage,
          secondaryImage,
          title: 'Your Fleet Equipment Hub',
          subtitle: 'Complete fleet equipment and accessories store',
          features: ['Wide Selection', 'Fast Shipping', 'Expert Support', 'Best Prices']
        };
      case 'geotab-equipment':
        return {
          mainImage,
          secondaryImage,
          title: 'Professional Fleet Equipment',
          subtitle: 'High-quality equipment solutions for fleet management',
          features: ['Durable Design', 'Easy Maintenance', 'Cost Effective', 'Expert Installation']
        };
      case 'geotab-keyless':
      case 'fleet-keyless':
      case 'geotab-key':
      case 'keyless-fleet':
        return {
          mainImage,
          secondaryImage,
          title: 'Keyless Fleet Management',
          subtitle: 'Advanced keyless entry and fleet access control',
          features: ['Remote Access', 'Security Alerts', 'User Management', 'Mobile Control']
        };
      case 'fleet-hours-of-service':
        return {
          mainImage,
          secondaryImage,
          title: 'ELD Compliance Made Simple',
          subtitle: 'Electronic logging device solutions for hours of service compliance',
          features: ['FMCSA Compliant', 'Easy Setup', 'Real-time Logs', 'Driver Friendly']
        };
      case 'go-insights':
        return {
          mainImage,
          secondaryImage,
          title: 'Fleet Analytics & Insights',
          subtitle: 'Advanced analytics and insights for fleet optimization',
          features: ['Real-time Data', 'Custom Reports', 'Predictive Analytics', 'Performance Metrics']
        };
      case 'road-eazy':
        return {
          mainImage,
          secondaryImage,
          title: 'Easy Road Monitoring',
          subtitle: 'Simple and effective road monitoring solutions',
          features: ['Live Monitoring', 'Alert System', 'Route Optimization', 'Cost Tracking']
        };
      case 'fleet-dashcam':
        return {
          mainImage,
          secondaryImage,
          title: 'Professional Fleet DashCams',
          subtitle: 'High-quality dash cameras designed for fleet operations',
          features: ['Fleet Management', 'Driver Safety', 'Incident Recording', 'Cost Reduction']
        };
      case 'smarter-tracking':
        return {
          mainImage,
          secondaryImage,
          title: 'Intelligent Tracking for Smart Business',
          subtitle: 'Transform your operations with our cutting-edge tracking solutions',
          features: ['Real-time Tracking', 'Smart Analytics', 'Cost Optimization', 'Easy Integration']
        };
      case 'fleet-flix':
        return {
          mainImage,
          secondaryImage,
          title: 'Fleet Video Streaming',
          subtitle: 'Stream and manage fleet video content with ease',
          features: ['Live Streaming', 'Video Management', 'Cloud Storage', 'Mobile Access']
        };
      default:
        return {
          mainImage,
          secondaryImage,
          title: 'Professional Fleet Solutions',
          subtitle: 'Advanced technology solutions for modern fleets',
          features: ['Quality Products', 'Expert Support', 'Reliable Service', 'Cost Effective']
        };
    }
  };

  const heroContent = getHeroContent();

  return (
    <div className="flex flex-col min-h-screen">
      {/* Hero Section with Photos */}
      <VariantManager 
        variant={variant} 
        heroContent={heroContent} 
        brand={brand} 
        domainConfig={domainConfig}
      />

            {/* Image Slider Section */}
            <VariantImageSlider />

            {/* Feature Section - Component handles domain config internally */}
            <VariantFeatures />

            {/* Testimonials Section - Component handles domain config internally */}
            <VariantTestimonials />
    </div>
  );
};

export default Home; 